'use client';

import { useEffect, useCallback, useState } from 'react';
import Link from 'next/link';
import { JobResponse, Series, FeedbackType } from '@/lib/types';
import api from '@/lib/api';
import { useToast } from './Toast';
import VideoGenerateModal from './VideoGenerateModal';
import FeedbackButtons from './FeedbackButtons';

interface ImageModalProps {
  job: JobResponse;
  jobs: JobResponse[]; // All gallery jobs for navigation
  onClose: () => void;
  onReuseParams: (job: JobResponse) => void;
  onDelete: (jobId: string) => void;
  onNavigate: (job: JobResponse) => void; // Navigate to another job
  series?: Series[];
  onSeriesChange?: () => void;
  onVideoCreated?: () => void;
}

export default function ImageModal({
  job,
  jobs,
  onClose,
  onReuseParams,
  onDelete,
  onNavigate,
  series = [],
  onSeriesChange,
  onVideoCreated
}: ImageModalProps) {
  const toast = useToast();
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState<FeedbackType | null>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  // Find current index and adjacent jobs
  const currentIndex = jobs.findIndex(j => j.job_id === job.job_id);
  const prevJob = currentIndex > 0 ? jobs[currentIndex - 1] : null;
  const nextJob = currentIndex < jobs.length - 1 ? jobs[currentIndex + 1] : null;

  const handleSeriesChange = async (newSeriesId: number | null) => {
    try {
      await api.updateJobSeries(job.job_id, newSeriesId);
      onSeriesChange?.();
      const seriesName = newSeriesId ? series.find(s => s.id === newSeriesId)?.name : null;
      toast.success(seriesName ? `Moved to "${seriesName}"` : 'Removed from series');
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleDelete = useCallback(() => {
    // Determine which job to navigate to after deletion (prefer next, fallback to prev)
    const navigateToJob = nextJob || prevJob;

    onDelete(job.job_id);

    if (navigateToJob) {
      // Navigate to next available image
      onNavigate(navigateToJob);
    } else {
      // No more images left, close the modal
      onClose();
    }
  }, [onDelete, job.job_id, onClose, onNavigate, nextJob, prevJob]);

  // Fetch feedback when job changes
  useEffect(() => {
    if (job.images[0]) {
      setCurrentFeedback(null);
      api.getFeedback(job.images[0]).then((result) => {
        setCurrentFeedback(result?.feedback ?? null);
      });
    }
  }, [job.images]);

  // Handle feedback toggle
  const handleFeedbackToggle = useCallback(async (feedbackType: FeedbackType) => {
    if (feedbackLoading || !job.images[0]) return;

    setFeedbackLoading(true);
    try {
      if (currentFeedback === feedbackType) {
        // Toggle off
        await api.removeFeedback(job.images[0]);
        setCurrentFeedback(null);
        toast.success(feedbackType === 'like' ? 'Removed like' : 'Removed dislike');
      } else {
        // Set feedback
        await api.setFeedback({
          image_path: job.images[0],
          image_type: 'generated',
          feedback: feedbackType,
          job_id: job.job_id,
        });
        setCurrentFeedback(feedbackType);
        toast.success(feedbackType === 'like' ? 'Liked!' : 'Disliked');
      }
    } catch (error) {
      toast.error('Failed to update feedback');
    } finally {
      setFeedbackLoading(false);
    }
  }, [job.images, job.job_id, currentFeedback, feedbackLoading, toast]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft' && prevJob) {
      onNavigate(prevJob);
    } else if (e.key === 'ArrowRight' && nextJob) {
      onNavigate(nextJob);
    } else if (e.key === 'Delete') {
      handleDelete();
    } else if (e.key.toLowerCase() === 'l' || e.key.toLowerCase() === 'g') {
      handleFeedbackToggle('like');
    } else if (e.key.toLowerCase() === 'd' || e.key.toLowerCase() === 'b') {
      handleFeedbackToggle('dislike');
    }
  }, [onClose, onNavigate, prevJob, nextJob, handleDelete, handleFeedbackToggle]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  if (job.status !== 'completed' || job.images.length === 0) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/95 animate-fadeIn flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 h-14 bg-black/50 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer"
            title="Close (Esc)"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <span className="text-white/40 text-sm">
            {currentIndex + 1} / {jobs.length}
          </span>
        </div>
        <p className="text-white/80 text-sm max-w-2xl whitespace-pre-wrap break-words max-h-24 overflow-y-auto">{job.prompt}</p>
        <div className="flex items-center gap-2">
          <Link
            href={`/img2img?source=${job.job_id}`}
            className="p-2 text-white/60 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-all cursor-pointer"
            title="Transform with Img2Img"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
            </svg>
          </Link>
          <button
            onClick={() => setShowVideoModal(true)}
            className="p-2 text-white/60 hover:text-purple-400 hover:bg-purple-500/10 rounded-lg transition-all cursor-pointer"
            title="Generate Video"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 010 1.972l-11.54 6.347a1.125 1.125 0 01-1.667-.986V5.653z" />
            </svg>
          </button>
          <a
            href={api.getImageUrl(job.images[0])}
            download
            className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer"
            title="Download"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
          </a>
          <div className="w-px h-6 bg-white/20" />
          <FeedbackButtons
            imagePath={job.images[0]}
            imageType="generated"
            jobId={job.job_id}
            initialFeedback={currentFeedback}
            onFeedbackChange={setCurrentFeedback}
            size="md"
            showShortcuts
          />
          <div className="w-px h-6 bg-white/20" />
          <button
            onClick={handleDelete}
            className="p-2 text-white/60 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all cursor-pointer"
            title="Delete (Del)"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
          </button>
        </div>
      </div>

      {/* Main image area with navigation */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        {/* Previous button */}
        {prevJob && (
          <button
            onClick={() => onNavigate(prevJob)}
            className="absolute left-4 z-10 p-3 text-white/40 hover:text-white bg-black/30 hover:bg-black/50 rounded-full transition-all cursor-pointer"
            title="Previous (←)"
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
        )}

        {/* Image */}
        <div className="max-w-[90vw] max-h-[calc(100vh-14rem)]">
          <img
            src={api.getImageUrl(job.images[0])}
            alt={job.prompt}
            className="max-w-full max-h-[calc(100vh-14rem)] object-contain cursor-pointer"
            onClick={onClose}
            title="Click to close"
          />
        </div>

        {/* Next button */}
        {nextJob && (
          <button
            onClick={() => onNavigate(nextJob)}
            className="absolute right-4 z-10 p-3 text-white/40 hover:text-white bg-black/30 hover:bg-black/50 rounded-full transition-all cursor-pointer"
            title="Next (→)"
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        )}
      </div>

      {/* Bottom bar with details */}
      <div className="bg-black/50 backdrop-blur-sm border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-3">
          {/* Parameters row */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-6 text-sm">
              <div>
                <span className="text-white/40">Size </span>
                <span className="text-white/80">{job.width}x{job.height}</span>
              </div>
              <div>
                <span className="text-white/40">Steps </span>
                <span className="text-white/80">{job.steps}</span>
              </div>
              <div>
                <span className="text-white/40">CFG </span>
                <span className="text-white/80">{job.cfg}</span>
              </div>
              <div>
                <span className="text-white/40">Seed </span>
                <span className="text-white/80">{job.seed ?? 'Random'}</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Series selector */}
              {series.length > 0 && (
                <select
                  value={job.series_id ?? ''}
                  onChange={(e) => handleSeriesChange(e.target.value ? parseInt(e.target.value) : null)}
                  className="bg-white/10 text-white/80 text-sm py-1.5 px-3 rounded-lg border border-white/20 focus:border-white/40 outline-none cursor-pointer"
                >
                  <option value="" className="bg-gray-900">No Series</option>
                  {series.map((s) => (
                    <option key={s.id} value={s.id} className="bg-gray-900">{s.name}</option>
                  ))}
                </select>
              )}

              <button
                onClick={() => onReuseParams(job)}
                className="px-4 py-1.5 bg-white/10 hover:bg-white/20 text-white/80 hover:text-white text-sm rounded-lg transition-all cursor-pointer"
              >
                Use Settings
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Video Generate Modal */}
      {showVideoModal && (
        <VideoGenerateModal
          sourceJob={job}
          onClose={() => setShowVideoModal(false)}
          onSuccess={() => {
            onVideoCreated?.();
          }}
        />
      )}
    </div>
  );
}
